import { AcompanhamentoSaudeRelatorio } from 'api/AcompanhamentoSaudeRelatorio'
import { AxiosPromise } from 'axios'
import { AgeRangeValue } from 'components/date/AgeRange'
import { EquipeSelectModel, LocalDateRange, RadioOption } from 'components/form'
import { CiapCidSelectModel } from 'components/form/field/select/CiapCidSelectField/CiapCidSelectField'
import { GrupoCondicaoSelectModel } from 'components/form/field/select/GrupoCondicaoSelectField/GrupoCondicaoSelectField'
import {
  AcompanhamentoCondicaoSaudeQueryInput,
  AgeRangeType,
  FaixaEtariaEnum,
  FaixaEtariaInput,
  GrupoCondicaoEnum,
  IvcfSubgrupoPerguntaEnum,
  PageParams,
  PeriodoCadastroIndividualEnum,
  RiscoVulnerabilidadeIvcfEnum,
} from 'graphql/types.generated'

import { AcompanhamentoFilterModel } from '../models/model-acompanhamentoFilter'

export const ACOMPANHAMENTO_CONDICAO_SAUDE_ATMOSPHERE_PATH = 'acompanhamento-condicao-saude'

export enum AgeRangeFilter {
  TODAS_FAIXAS = 'TODAS_FAIXAS',
  CRIANCA = 'CRIANCA',
  ADOLESCENTE = 'ADOLESCENTE',
  ADULTO = 'ADULTO',
  IDOSO = 'IDOSO',
  OUTRA = 'OUTRA',
}

export interface AcompanhamentoCondicaoSaudeFormModel extends AcompanhamentoFilterModel {
  unidadeResponsavelId?: ID
  equipeResponsavelId?: ID
  equipeResponsavel?: EquipeSelectModel
  isCoordenador?: boolean
  microareas?: string[]
  foraarea?: boolean
  naoinformada?: boolean
  grupoCondicao?: GrupoCondicaoSelectModel[]
  problemasCondicoes?: CiapCidSelectModel[]
  ativoListaProblema?: boolean
  periodoUltimoAtendimento?: LocalDateRange
  ivcf?: IvcfFilterModel
  tria?: TriaFilterModel
  tipoVulnerabilidadeSelected?: TipoVulnerabilidadeEnum
  // controle dos grupos rápidos
  isDiabetesCheck?: boolean
  isHipertensaoCheck?: boolean
  isObesidadeCheck?: boolean
  isGravidezCheck?: boolean
  pageParams?: PageParams
}

export const condicaoToFormRecord: Partial<Record<GrupoCondicaoEnum, keyof AcompanhamentoCondicaoSaudeFormModel>> = {
  [GrupoCondicaoEnum.DIABETES]: 'isDiabetesCheck',
  [GrupoCondicaoEnum.HIPERTENSAO_ARTERIAL]: 'isHipertensaoCheck',
  [GrupoCondicaoEnum.OBESIDADE]: 'isObesidadeCheck',
  [GrupoCondicaoEnum.GRAVIDEZ]: 'isGravidezCheck',
}

export interface AcompanhamentoCondicaoSaudeDto {
  idCidadao: ID
  nomeCidadao?: string
  nomeSocialCidadao?: string
  cpfCidadao?: string
  cnsCidadao?: string
  telefoneResidencial?: string
  telefoneCelular?: string
  telefoneContato?: string
  endereco?: string
  dataUltimoAtendimento?: Date
  ivcfRiscoVulnerabilidade?: RiscoVulnerabilidadeIvcfEnum
  ivcfDataResultado?: Date
}

export const faixaEtariaRecord: Partial<Record<FaixaEtariaEnum, FaixaEtariaInput>> = {
  [FaixaEtariaEnum.CRIANCA]: { idadeMaxima: 10, tipoFaixaEtaria: AgeRangeType.ANOS },
  [FaixaEtariaEnum.ADOLESCENTE]: { idadeMinima: 11, idadeMaxima: 19, tipoFaixaEtaria: AgeRangeType.ANOS },
  [FaixaEtariaEnum.ADULTO]: { idadeMinima: 20, idadeMaxima: 59, tipoFaixaEtaria: AgeRangeType.ANOS },
  [FaixaEtariaEnum.IDOSO]: { idadeMinima: 60, tipoFaixaEtaria: AgeRangeType.ANOS },
}

export interface IvcfFilterModel {
  riscoVulnerabilidade?: RiscoVulnerabilidadeIvcfEnum[]
  subgrupo?: IvcfSubgrupoPerguntaEnum[]
  pontuacao?: AgeRangeValue
  buscaPorPontuacao?: boolean
  exibirCidadaoIdosoSemIvcf?: boolean
}

export interface TriaFilterModel {
  periodoRegistro?: PeriodoCadastroIndividualEnum
  resultadosTria?: ResultadosTriaEnum
}

export enum TipoVulnerabilidadeEnum {
  TRIA = 'TRIA',
  IVCF = 'IVCF',
}

export enum ResultadosTriaEnum {
  RISCO_DE_INSEGURANCA_ALIMENTAR = 'RISCO_DE_INSEGURANCA_ALIMENTAR',
  SEM_REGISTRO_TRIA_NO_PERIODO = 'SEM_REGISTRO_TRIA_NO_PERIODO',
}

export const resultadosTriaOptions: RadioOption<ResultadosTriaEnum>[] = [
  {
    value: ResultadosTriaEnum.RISCO_DE_INSEGURANCA_ALIMENTAR,
    label: 'Exibir apenas cidadãos que vivem em domicílios em risco de insegurança alimentar',
  },
  {
    value: ResultadosTriaEnum.SEM_REGISTRO_TRIA_NO_PERIODO,
    label: 'Exibir apenas cidadãos que vivem em domicílios sem registro da TRIA no período',
  },
]

export const periodoRegistroTriaRecord: Record<PeriodoCadastroIndividualEnum, string> = {
  [PeriodoCadastroIndividualEnum.SEIS_MESES]: 'Últimos 6 meses',
  [PeriodoCadastroIndividualEnum.DOZE_MESES]: 'Últimos 12 meses',
}

export interface RelatorioOperacionalAuthorization {
  hasCriancaPermission: boolean
  hasGestantePermission: boolean
  hasHipertensaoPermission: boolean
}

export interface RelatoriosOperacionaisModel {
  tipoRelatorioOperacionalEnum: TipoRelatorioOperacionalEnum
}

export enum TipoRelatorioOperacionalEnum {
  CRIANCA = 'CRIANCA',
  GESTANTE = 'GESTANTE',
  RISCO_CARDIOVASCULAR = 'RISCO_CARDIOVASCULAR',
}

export const tipoRelatorioOperacionalLabel: Record<TipoRelatorioOperacionalEnum, string> = {
  [TipoRelatorioOperacionalEnum.CRIANCA]: 'Relatório operacional de Crianças menores de 5 anos',
  [TipoRelatorioOperacionalEnum.GESTANTE]: 'Relatório operacional de Gestante/Puérpera',
  [TipoRelatorioOperacionalEnum.RISCO_CARDIOVASCULAR]: 'Relatório operacional de Risco cardiovascular',
}

export const tipoRelatorioOperacionalAnalyticsLabel: Record<TipoRelatorioOperacionalEnum, string> = {
  [TipoRelatorioOperacionalEnum.CRIANCA]: 'relatorio_ope_acomp_cond_saude_with_crianca',
  [TipoRelatorioOperacionalEnum.GESTANTE]: 'relatorio_ope_acomp_cond_saude_with_gestante_puerpera',
  [TipoRelatorioOperacionalEnum.RISCO_CARDIOVASCULAR]: 'relatorio_ope_acomp_cond_saude_with_risco_cardiovascular',
}

export const tipoRelatorioOperacionalToApiCallRecord: Record<
  TipoRelatorioOperacionalEnum,
  { api: (filter: AcompanhamentoCondicaoSaudeQueryInput) => AxiosPromise; fileName: string }
> = {
  [TipoRelatorioOperacionalEnum.CRIANCA]: {
    api: (filter) => new AcompanhamentoSaudeRelatorio().downloadRelatorioOperacionalCrianca(filter),
    fileName: 'relatorio-operacional-de-criancas-menores-de-5-anos',
  },
  [TipoRelatorioOperacionalEnum.GESTANTE]: {
    api: (filter) => new AcompanhamentoSaudeRelatorio().downloadRelatorioOperacionalGestante(filter),
    fileName: 'relatorio-operacional-de-gestante-puerpera',
  },
  [TipoRelatorioOperacionalEnum.RISCO_CARDIOVASCULAR]: {
    api: (filter) => new AcompanhamentoSaudeRelatorio().downloadRelatorioOperacionalRiscoCardiovascular(filter),
    fileName: 'relatorio-operacional-de-risco-cardiovascular',
  },
}
